import type { LineItemSummaries } from './purchaseOrder';

interface DeliveryNoteBase {
  id: string;
  deliveryNumber?: string;
  deliveryDate: string;
  description: string;
  fileKey?: string;
  fileName?: string;
  mimetype?: string;
  checksum?: string;
  amountDelivered?: number;
  items?: DeliveryNoteItem[];
}

export type DeliveryNoteItem = {
  purchaseOrderItemId: string;
  quantity: number;
};

export interface DeliveryNoteWithAmount extends DeliveryNoteBase {
  items: undefined;
  amountDelivered: NonNullable<DeliveryNoteBase['amountDelivered']>;
}

export interface DeliveryNoteWithItems extends DeliveryNoteBase {
  items: NonNullable<DeliveryNoteBase['items']>;
  amountDelivered: undefined;
}

export type DeliveryNote = DeliveryNoteWithAmount | DeliveryNoteWithItems;

export type DeliveryNotesStatuses =
  | 'fullyDelivered'
  | 'partiallyDelivered'
  | 'waitingForDelivery'
  | 'noDeliveryExpected'
  | 'overDelivered'
  | 'error';

export type DeliveryNoteFileViewable = {
  viewUrl?: string;
};

export type DeliveryNotesItemsQuantityDelivered = {
  quantityDelivered: number;
  totalToDeliver: number;
  items: {
    id: string;
    description: string;
    quantityDelivered: number;
    totalToDeliver: number;
    unitPrice: number;
  }[];
};

export type DeliveryNotesAmountDelivered = {
  amountDelivered: number;
  totalAmount: number;
};

export type DeliveryNotesQuantityDelivered =
  | DeliveryNotesItemsQuantityDelivered
  | DeliveryNotesAmountDelivered;

export const isDeliveryNoteWithAmount = (
  deliveryNote: DeliveryNote,
): deliveryNote is DeliveryNoteWithAmount =>
  deliveryNote.items === undefined || deliveryNote.items.length === 0;

export const isDeliveryNotesAmountDelivered = (
  deliveryNotesQuantityDelivered: DeliveryNotesQuantityDelivered,
): deliveryNotesQuantityDelivered is DeliveryNotesAmountDelivered =>
  'totalAmount' in deliveryNotesQuantityDelivered;

export const computeTotalQuantityDelivered = (
  deliveryNoteWithItems: DeliveryNoteWithItems,
): number =>
  deliveryNoteWithItems.items.reduce((total, item) => total + item.quantity, 0);

export const computeItemDeliveryStatus = (
  itemDetails:
    | DeliveryNotesItemsQuantityDelivered['items'][number]
    | LineItemSummaries['purchaseOrderLines'][number],
): DeliveryNotesStatuses => {
  const totalToDeliver = Object.hasOwn(itemDetails, 'totalToDeliver')
    ? (itemDetails as DeliveryNotesItemsQuantityDelivered['items'][number])
        .totalToDeliver
    : (itemDetails as LineItemSummaries['purchaseOrderLines'][number]).quantity;
  if (itemDetails.quantityDelivered === 0) {
    return 'waitingForDelivery';
  }

  if (itemDetails.quantityDelivered === totalToDeliver) {
    return 'fullyDelivered';
  }

  return 'partiallyDelivered';
};

export const hasReceivedDeliveries = (status?: DeliveryNotesStatuses) => {
  if (!status) {
    return false;
  }
  return ['fullyDelivered', 'partiallyDelivered', 'overDelivered'].includes(
    status,
  );
};
